import { SeoHead } from '@/components/SeoHead';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnnouncementBar } from '@/components/AnnouncementBar';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MorphingText } from '@/components/ui/MorphingText';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { withAudienceEntityType } from '@/lib/auth-url';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useLandingNavigationPaths } from '@/hooks/useLandingNavigationPaths';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { UrgencyBanner } from '@/components/dowod/UrgencyBanner';
import { ApprovalFlowVisual } from '@/components/dowod/ApprovalFlowVisual';
import { useTranslation, type TranslationKey } from '@/i18n/i18n';
import { AiAccountingLink } from '@/components/AiAccountingLink';
import { AI_ACCOUNTING_ANCHORS } from '@/content/ai-accounting-anchors';
import chatEvidence from '@/assets/renders/chat-shell-question-evidence-2x.webp';
import chatEvidence1440 from '@/assets/renders/chat-shell-question-evidence-1440w.webp';
import chatEvidence720 from '@/assets/renders/chat-shell-question-evidence-720w.webp';
import chatAnswer from '@/assets/renders/chat-shell-answer-rates-2x.webp';
import chatAnswer1440 from '@/assets/renders/chat-shell-answer-rates-1440w.webp';
import chatAnswer720 from '@/assets/renders/chat-shell-answer-rates-720w.webp';

const seoContent = {
  pl: {
    title: 'Bookeper dla JDG — podatki policzone i sprawdzone',
    description:
      'Osobisty księgowy AI dla jednoosobowej działalności: pytasz o podatki, agent odpowiada ze źródłami. ZUS DRA, JPK i PIT przygotowane do Twojego zatwierdzenia.',
    url: 'https://www.bookeper.pl/jdg',
  },
  en: {
    title: 'Bookeper for Sole Traders — taxes calculated and checked',
    description:
      'A personal AI accountant for Polish sole proprietors: ask a tax question, the agent answers with sources. ZUS DRA, SAF-T and PIT prepared for your approval.',
    url: 'https://www.bookeper.pl/en/jdg',
  },
  uk: {
    title: 'Bookeper для ФОП у Польщі (JDG) — податки пораховано і перевірено',
    description:
      'Персональний AI-бухгалтер для ФОП у Польщі: поставте податкове запитання — агент відповість із джерелами. ZUS DRA, SAF-T і PIT підготовлені до вашого затвердження.',
    url: 'https://www.bookeper.pl/uk/jdg',
  },
} as const;

/** Pain rotator inherited from the retired homepage hero (BOO-2852 → Wave 2 R5). */
const painKeys = [
  'hero.pain1',
  'hero.pain2',
  'hero.pain3',
  'hero.pain4',
  'hero.pain5',
  'hero.pain6',
  'hero.pain7',
  'hero.pain8',
] as const satisfies readonly TranslationKey[];

/**
 * Real chat-shell captures (KTD1/KTD8, src/assets/renders/README.md): the
 * ACTUAL running product answering a ryczałt question — question + evidence
 * card, then the answer with the rate table. Never a simulated chat UI
 * (DESIGN.md binding rule).
 */
const shellCaptures: Array<{
  src: string;
  srcSet: string;
  altKey: TranslationKey;
}> = [
  {
    src: chatEvidence,
    srcSet: `${chatEvidence720} 720w, ${chatEvidence1440} 1440w, ${chatEvidence} 2880w`,
    altKey: 'jdg.shell.capture1.alt',
  },
  {
    src: chatAnswer,
    srcSet: `${chatAnswer720} 720w, ${chatAnswer1440} 1440w, ${chatAnswer} 2880w`,
    altKey: 'jdg.shell.capture2.alt',
  },
];

/** Condensed how-it-works: the 3 JDG-relevant steps of the homepage's 5. */
const stepKeys = [
  { title: 'howItWorks.step3.title', description: 'howItWorks.step3.description' },
  { title: 'howItWorks.step4.title', description: 'howItWorks.step4.description' },
  { title: 'howItWorks.step5.title', description: 'howItWorks.step5.description' },
] as const;

function SignupCta({ source }: { source: string }) {
  const { t } = useTranslation();
  const signupUrl = useSignupUrl({ entityType: 'jdg', source });

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button size="lg" className="h-14 px-8 text-base font-bold rounded-full" asChild>
          <a href={signupUrl} onClick={() => trackLandingCtaClicked(source, t('hero.ctaPrimary'))}>
            {t('hero.ctaPrimary')}
            <ArrowRight className="ml-2 size-5" aria-hidden="true" />
          </a>
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {[t('hero.noCommitment'), t('hero.setupTime')].map((item) => (
          <span key={item} className="inline-flex items-center gap-1.5">
            <Check className="size-3.5 text-success" aria-hidden="true" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Jdg() {
  const { t, locale } = useTranslation();
  const seo = seoContent[locale];
  const heroRef = useLandingSectionTracking('jdg_hero');
  const shellRef = useLandingSectionTracking('jdg_shell');
  const ksefRef = useLandingSectionTracking('jdg_ksef');
  const ctaRef = useLandingSectionTracking('jdg_cta');
  const { withSearch } = useLandingNavigationPaths();
  const localizedKsefPath = withAudienceEntityType(withSearch('/ksef-automatycznie'), 'jdg');

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <SeoHead>
        <html lang={locale} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:type" content="website" />
        <link rel="alternate" hrefLang="pl" href={seoContent.pl.url} />
        <link rel="alternate" hrefLang="en" href={seoContent.en.url} />
        <link rel="alternate" hrefLang="uk" href={seoContent.uk.url} />
        <link rel="alternate" hrefLang="x-default" href={seoContent.pl.url} />
      </SeoHead>
      <AnnouncementBar />
      <Navigation />
      <main>
        {/* Hero — the pain rotator retired from the homepage lives on here (R5) */}
        <section
          ref={heroRef}
          className="px-6 md:px-8 pt-16 pb-14 bg-background border-b border-border"
          aria-labelledby="jdg-hero-heading"
        >
          <div className="max-w-6xl mx-auto">
            <Badge variant="outline" className="mb-5">
              {t('hero.badge')}
            </Badge>
            <h1
              id="jdg-hero-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.2] max-w-3xl"
            >
              {t('hero.heading')}
              <br />
              <MorphingText
                phrases={painKeys.map((key) => t(key))}
                interval={3000}
                className="text-gradient-yellow"
              />
            </h1>
            {/* Proof-led divergence from the homepage subheading (design doc 3a
                item 6, judge round 1 S2/B2): jdg leads with cited-number density,
                not a duplicate of the home claim. */}
            <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              {t('jdg.hero.subheading')}
            </p>
            {locale === 'pl' ? (
              <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                Co robi AI, a co liczy kod? Zobacz, czym jest{' '}
                <AiAccountingLink anchor={AI_ACCOUNTING_ANCHORS.jdg} />.
              </p>
            ) : null}
            <div className="mt-8">
              <SignupCta source="jdg_hero" />
            </div>
          </div>
        </section>

        {/* Real chat shell — actual captures, never a simulated chat UI */}
        <section
          ref={shellRef}
          className="px-6 md:px-8 py-14 bg-muted/40 border-b border-border"
          aria-labelledby="jdg-shell-heading"
        >
          <div className="max-w-6xl mx-auto">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {t('jdg.shell.kicker')}
            </p>
            <h2
              id="jdg-shell-heading"
              className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('jdg.shell.heading')}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">{t('jdg.shell.subheading')}</p>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {shellCaptures.map(({ src, srcSet, altKey }) => (
                <figure
                  key={altKey}
                  className="rounded-2xl border border-foreground/20 bg-card overflow-hidden shadow-sm"
                >
                  <img
                    src={src}
                    srcSet={srcSet}
                    sizes="(min-width: 768px) min(50vw, 576px), calc(100vw - 48px)"
                    alt={t(altKey)}
                    width={2880}
                    height={1800}
                    loading="lazy"
                    className="w-full h-auto"
                  />
                  <figcaption className="p-4 text-sm text-muted-foreground leading-relaxed">
                    {t(altKey)}
                    <a
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-1 font-mono text-xs underline underline-offset-4 hover:no-underline text-foreground"
                    >
                      {t('proof.viewFull')}
                      <ArrowUpRight className="size-3" aria-hidden="true" />
                    </a>
                  </figcaption>
                </figure>
              ))}
            </div>

            {/* Honest disclaimer — visible text, not a tooltip (DESIGN.md / R5) */}
            <p className="mt-6 font-mono text-sm text-muted-foreground">
              {t('jdg.shell.disclaimer')}
            </p>
          </div>
        </section>

        {/* How it works — condensed to the 3 JDG steps */}
        <section
          className="px-6 md:px-8 py-14 border-b border-border"
          aria-labelledby="jdg-how-heading"
        >
          <div className="max-w-6xl mx-auto">
            <h2 id="jdg-how-heading" className="text-3xl md:text-4xl font-semibold tracking-tight">
              {t('jdg.how.heading')}
            </h2>
            <ol className="mt-8 grid md:grid-cols-3 gap-8">
              {stepKeys.map((step, index) => (
                <li key={step.title}>
                  <p className="font-mono text-sm text-muted-foreground">0{index + 1}</p>
                  <h3 className="mt-2 text-xl font-semibold">{t(step.title)}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">
                    {t(step.description)}
                  </p>
                </li>
              ))}
            </ol>
            <div className="mt-10 max-w-2xl">
              <ApprovalFlowVisual />
            </div>
          </div>
        </section>

        {/* KSeF story + locale-aware cross-link (R8) */}
        <section
          ref={ksefRef}
          className="px-6 md:px-8 py-14 border-b border-border"
          aria-labelledby="jdg-ksef-heading"
        >
          <div className="max-w-6xl mx-auto">
            <h2 id="jdg-ksef-heading" className="text-3xl md:text-4xl font-semibold tracking-tight">
              {t('jdg.ksef.heading')}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">{t('jdg.ksef.description')}</p>
            <Link
              to={localizedKsefPath}
              className="mt-4 inline-flex items-center gap-1 text-sm underline underline-offset-4 hover:no-underline"
            >
              {t('jdg.ksef.link')}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
            {/* Proof-then-pressure (BOO-3240 KTD6): urgency renders only after
                the evidence sections above — never near the hero. */}
            <div className="mt-8 max-w-2xl">
              <UrgencyBanner />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section ref={ctaRef} className="px-6 md:px-8 py-16" aria-labelledby="jdg-cta-heading">
          <div className="max-w-6xl mx-auto">
            <h2 id="jdg-cta-heading" className="text-3xl md:text-4xl font-semibold tracking-tight">
              {t('jdg.cta.heading')}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">{t('jdg.cta.subheading')}</p>
            <div className="mt-8">
              <SignupCta source="jdg_cta" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}
